import React from 'react';
import { RulesResult } from '../types';

interface Props {
  result: RulesResult | null;
  loading?: boolean;
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return '$' + Math.round(n).toLocaleString('en-US');
}
function fmtPct(n: number | null | undefined, decimals = 2): string {
  if (n == null) return '—';
  return (n * 100).toFixed(decimals) + '%';
}
function fmt2(n: number | null | undefined, decimals = 2): string {
  if (n == null) return '—';
  return n.toFixed(decimals);
}

function Row({ label, value, sub, highlight, red }: {
  label: string; value: string; sub?: string; highlight?: boolean; red?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-gray-400 text-xs">{label}</span>
      <div className="text-right">
        <span className={`font-mono text-sm ${red ? 'text-[#EF4444]' : highlight ? 'text-[#2563EB] font-bold' : 'text-white'}`}>
          {value}
        </span>
        {sub && <span className="block text-xs text-gray-600 font-mono">{sub}</span>}
      </div>
    </div>
  );
}

function RatioBar({ label, value, max, breached }: {
  label: string; value: string; max?: string; breached?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-gray-400 text-xs">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`font-mono text-sm font-semibold ${breached ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
          {value}
        </span>
        {max && <span className="text-gray-600 text-xs font-mono">/ {max}</span>}
        <span className="text-base leading-none">{breached ? '🔴' : '🟢'}</span>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="border-t border-gray-800 my-1" />;
}

export default function RatioPanel({ result, loading }: Props) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-[#1A1D27] rounded-lg p-4 animate-pulse border border-gray-800">
            <div className="h-3 bg-gray-700 rounded w-1/3 mb-3" />
            <div className="space-y-2">
              <div className="h-3 bg-gray-700 rounded" />
              <div className="h-3 bg-gray-700 rounded w-4/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!result) {
    return (
      <div className="bg-[#1A1D27] rounded-lg p-6 text-center border border-gray-800">
        <div className="text-gray-500 text-sm">Fill in the loan form to see live calculations</div>
      </div>
    );
  }

  const tierColor =
    result.borrowerTier === 'Tier1'      ? 'bg-[#10B981]/20 text-[#34D399] border-[#10B981]/40' :
    result.borrowerTier === 'Tier2'      ? 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/40' :
    result.borrowerTier === 'Ineligible' ? 'bg-[#EF4444]/20 text-[#F87171] border-[#EF4444]/40' :
    'bg-gray-700/20 text-gray-400 border-gray-700';

  const hasLTAIV = result.ltaiv != null;
  const hasLTARV = result.ltarv != null;

  const loanAmountFull = (result.loanAtFunding ?? 0) + (result.constructionHoldback ?? 0);

  return (
    <div className="space-y-3">

      {/* ──── PANEL 1: LOAN STRUCTURE ──── */}
      <div className="bg-[#1A1D27] rounded-lg p-4 border border-gray-800">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-mono text-gray-500 uppercase tracking-wider">Loan Structure</div>
          {result.borrowerTier && (
            <div className={`inline-flex items-center px-2.5 py-1 rounded border font-mono font-bold text-xs ${tierColor}`}>
              {result.borrowerTier}
            </div>
          )}
        </div>

        <div className="divide-y divide-gray-800/50">
          <Row label="Purchase / Financing Basis" value={fmtMoney(result.totalProjectCost)} />
          <Row label="Construction Holdback"       value={fmtMoney(result.constructionHoldback)} />
          <Row label="Total Fees"                  value={fmtMoney(result.totalLoanFees)} />
        </div>

        <Divider />

        <div className="divide-y divide-gray-800/50">
          <Row label="Loan Amount"      value={fmtMoney(loanAmountFull)} highlight />
          <Row label="Interest Reserve" value={fmtMoney(result.interestReserve)} />
          <Row label="Holdback"         value={fmtMoney(result.constructionHoldback)} />
          <Row label="Loan at Funding"  value={fmtMoney(result.loanAtFunding)} highlight />
        </div>

        <Divider />

        <div className="divide-y divide-gray-800/50">
          <RatioBar
            label="LTC"
            value={fmtPct(result.ltc)}
            max={result.maxLTC != null ? fmtPct(result.maxLTC) : undefined}
            breached={result.ltcBreached}
          />
          {hasLTAIV && (
            <RatioBar
              label="LTAIV"
              value={fmtPct(result.ltaiv)}
              max={result.maxLTAIV != null ? fmtPct(result.maxLTAIV) : undefined}
              breached={result.ltaivBreached}
            />
          )}
          {hasLTARV && (
            <RatioBar
              label="LTARV"
              value={fmtPct(result.ltarv)}
              max={result.maxLTARV != null ? fmtPct(result.maxLTARV) : undefined}
              breached={result.ltarvBreached}
            />
          )}
        </div>

        <Divider />

        <div className="divide-y divide-gray-800/50">
          <Row label="Per Diem"           value={`$${fmt2(result.perDiem, 2)}`} />
          <Row label="Initial Payment"    value={fmtMoney(result.initialPaymentAmount)} />
          <Row label="Max Payment"        value={fmtMoney(result.maxPaymentAmount)} />
          {(result.oddDaysInterest ?? 0) > 0 && (
            <Row label="Odd Days Interest" value={fmtMoney(result.oddDaysInterest)} />
          )}
          <Row label="Cash to Close"      value={fmtMoney(result.cashToClose)} highlight />
          {result.maxAllowedLoan != null && (
            <Row
              label="Max Allowed Loan"
              value={fmtMoney(result.maxAllowedLoan)}
              red={result.loanExceedsMax}
            />
          )}
        </div>
      </div>

      {/* ──── PANEL 2: PRODUCT & PRICING ──── */}
      {(result.maxLTC != null || result.minRate != null) && (
        <div className="bg-[#1A1D27] rounded-lg p-4 border border-gray-800">
          <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">
            Product &amp; Pricing Eligibility
          </div>

          <div className="divide-y divide-gray-800/50">
            {result.maxLTC   != null && <Row label="Max LTC"   value={fmtPct(result.maxLTC)}   />}
            {result.maxLTAIV != null && <Row label="Max LTAIV" value={fmtPct(result.maxLTAIV)} />}
            {result.maxLTARV != null && <Row label="Max LTARV" value={fmtPct(result.maxLTARV)} />}
          </div>

          <Divider />

          <div className="divide-y divide-gray-800/50">
            {result.minRate != null && (
              <Row
                label="Min Rate"
                value={fmtPct(result.adjustedMinRate ?? result.minRate)}
                sub={
                  result.priceAdjustment && result.priceAdjustment > 0
                    ? `+${fmtPct(result.priceAdjustment)} term adj`
                    : undefined
                }
                red={result.rateBelowMin}
              />
            )}
            {result.minOrigFee != null && (
              <Row label="Min Orig Fee" value={fmtPct(result.minOrigFee)} red={result.origFeeBelowMin} />
            )}
          </div>

          <Divider />

          <div className="divide-y divide-gray-800/50">
            {result.grossYield != null && (
              <Row label="Gross Yield" value={fmtPct(result.grossYield)} />
            )}
            <Row
              label="Min / Std / Max Term"
              value={`${result.minTerm ?? '—'} / ${result.standardTerm ?? '—'} / ${result.maxTerm ?? '—'} mo`}
              red={result.termExceedsMax || result.termBelowMin}
            />
          </div>
        </div>
      )}

      {/* ──── PANEL 3: CONDITIONS ──── */}
      {result.conditions && result.conditions.length > 0 && (
        <div className="bg-[#1A1D27] rounded-lg p-4 border border-gray-800">
          <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">
            Conditions (auto-generated)
          </div>
          <div className="space-y-2">
            {result.conditions.map((c, i) => (
              <div key={i} className="flex gap-2 text-xs text-gray-300 leading-relaxed">
                <span className="text-blue-400 mt-0.5 flex-shrink-0">🔵</span>
                <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
